import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EmptyState, SkeletonLoader, StatusChip } from '@shared/design-system/components';
import { useAuth } from '@shared/auth/useAuth';
import {
  KNOWN_FAILURE_SIGNATURES,
  compactKnowledge,
  createLesson,
  listLessons,
  listSupportSites,
  reviewLesson,
  seedStarterLessons,
  SupportApiError,
  type CompactApplyResult,
  type CompactPreviewResult,
  type RecoveryLesson,
  type SeedSkip,
  type SeedSkipReason,
} from '../../api/supportClient';
import { KnowledgePreview } from '../../components/KnowledgePreview';

/**
 * Pane 4 -- curating the AI recovery assistant's knowledge base
 * (AI-analyzer follow-up work, not one of the plan's original three
 * panes). Reading is any support role: seeing what the assistant was
 * taught is part of trusting its answers. APPROVING a lesson and
 * COMPACTING are `platform_admin` only -- an approved lesson steers
 * every future suggestion for that signature at every site until
 * someone notices it is wrong, which is a strictly narrower privilege
 * than running a runbook.
 */

const LESSON_STATUS_TABS = ['pending', 'approved', 'rejected', 'archived'] as const;

function statusChipKind(status: string): 'warning' | 'active' | 'error' | 'offline' {
  if (status === 'pending') return 'warning';
  if (status === 'approved') return 'active';
  if (status === 'rejected') return 'error';
  return 'offline'; // archived
}

function LessonRow({ lesson, canAdmin }: { lesson: RecoveryLesson; canAdmin: boolean }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: (status: 'approved' | 'rejected' | 'archived') => reviewLesson(lesson.id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['support', 'lessons'] });
      void qc.invalidateQueries({ queryKey: ['support', 'knowledge-preview'] });
    },
  });

  return (
    <li style={{ border: '1px solid var(--nx-border)', borderRadius: 'var(--nx-r)', padding: 12, marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--nx-muted)' }}>
          {lesson.failure_signature}
          {lesson.runbook_id ? ` · ${lesson.runbook_id}` : ''}
          {lesson.site ? ` · ${lesson.site}` : ' · cross-site'} · {lesson.source}
          {lesson.cited_count > 0 && ` · cited ${lesson.cited_count}×`}
        </div>
        <StatusChip status={statusChipKind(lesson.status)} label={lesson.status} />
      </div>
      <p style={{ margin: '6px 0' }}>{lesson.body}</p>
      {canAdmin && lesson.status === 'pending' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('approved')}>
            Approve
          </button>
          <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('rejected')}>
            Reject
          </button>
        </div>
      )}
      {canAdmin && lesson.status === 'approved' && (
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate('archived')}>
          Archive
        </button>
      )}
      {mutation.isError && (
        <div style={{ color: 'var(--nx-error)', fontSize: 12 }}>
          {mutation.error instanceof SupportApiError ? mutation.error.message : String(mutation.error)}
        </div>
      )}
    </li>
  );
}

// `KnowledgePreview` used to live here; it moved to
// `src/components/KnowledgePreview.tsx` (unchanged) so the Diagnose
// issue detail can render the same assistant-prompt preview for an
// issue's derived signature.

function ManualLessonForm({ signature }: { signature: string }) {
  const qc = useQueryClient();
  const sitesQuery = useQuery({ queryKey: ['support', 'sites'], queryFn: listSupportSites });
  const [body, setBody] = useState('');
  const [runbookId, setRunbookId] = useState('');
  const [site, setSite] = useState('');
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      createLesson({
        failure_signature: signature,
        body,
        runbook_id: runbookId || undefined,
        site: site || undefined,
      }),
    onSuccess: () => {
      setBody('');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['support', 'lessons'] });
      void qc.invalidateQueries({ queryKey: ['support', 'knowledge-preview'] });
    },
  });

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}>
        + Write a lesson manually
      </button>
    );
  }

  return (
    <div style={{ border: '1px solid var(--nx-border)', borderRadius: 'var(--nx-r)', padding: 12 }}>
      <p style={{ fontSize: 12, color: 'var(--nx-muted)' }}>
        Manually authored lessons are approved immediately — the review gate exists to catch
        auto-drafted text, and making you approve your own deliberate writing twice is ceremony.
      </p>
      <textarea
        placeholder={`What should the assistant know about "${signature}"?`}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        style={{ width: '100%', marginBottom: 6 }}
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
        <input
          placeholder="Runbook id (optional)"
          value={runbookId}
          onChange={(e) => setRunbookId(e.target.value)}
          style={{ flex: 1 }}
        />
        <select
          value={site}
          onChange={(e) => setSite(e.target.value)}
          aria-label="Lesson site"
          style={{ flex: 1 }}
        >
          {/* A picker, not free text: the backend validates the site
              and a typo would just 400. Blank is meaningful here --
              cross-site lessons are the most valuable kind. */}
          <option value="">Cross-site (all sites)</option>
          {(sitesQuery.data ?? []).map((s2) => (
            <option key={s2.site} value={s2.site}>
              {s2.label}
            </option>
          ))}
        </select>
      </div>
      {mutation.isError && (
        <div style={{ color: 'var(--nx-error)', fontSize: 12, marginBottom: 6 }}>
          {String(mutation.error)}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" disabled={!body.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
          Add approved lesson
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const SKIP_REASON_LABEL = {
  approved: 'already approved',
  pending: 'awaiting review',
  rejected: 'rejected',
  archived: 'archived',
  merged: 'merged by compaction',
} satisfies Record<SeedSkipReason, string>;
const SKIP_REASON_ORDER: string[] = Object.keys(SKIP_REASON_LABEL);

/** A reason this build does not know still shows, under its raw name,
 *  rather than dropping out of the count. */
function skipReasonLabel(reason: string): string {
  return (SKIP_REASON_LABEL as Record<string, string | undefined>)[reason] ?? reason;
}

/** "3 skipped — 2 already approved, 1 merged by compaction". Null when
 *  the breakdown would add nothing to "(already present)": no reasons
 *  reported (older api-server) or every skip is an approved starter
 *  already doing its job. */
function describeSkipReasons(reasons: SeedSkip[] | undefined): string | null {
  // Not `Array.isArray` for tidiness: an api-server older than this
  // console sends the previous record-keyed shape, and counting its
  // VALUES would report one skip per signature where several happened.
  // Saying nothing is better than saying a smaller number.
  if (!Array.isArray(reasons)) return null;
  const counts = new Map<string, number>();
  for (const { reason } of reasons) counts.set(reason, (counts.get(reason) ?? 0) + 1);
  if (counts.size === 0 || (counts.size === 1 && counts.has('approved'))) return null;
  const rank = (r: string) => {
    const i = SKIP_REASON_ORDER.indexOf(r);
    return i === -1 ? SKIP_REASON_ORDER.length : i;
  };
  const parts = [...counts.entries()]
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([r, n]) => `${n} ${skipReasonLabel(r)}`);
  return `${reasons.length} skipped — ${parts.join(', ')}`;
}

/**
 * One-click seeding of the cross-site starter lessons, distilled from
 * the pilot's incidents. NOT one per failure signature: the site's
 * failure modes do not arrive one per kind, so `task_stalled` carries
 * four and `robot_faulted` three. Gated the
 * same way as Approve: a seeded lesson is approved on arrival and
 * steers every site's suggestions for its signature. The server is
 * idempotent, so the button stays available and the result line says
 * how many were skipped as already present -- and, where the
 * api-server reports it, in which state each was found.
 *
 * `onSeeded` fires on success so the pane can switch the lesson list
 * to the `approved` tab: the rows just written land there, and under
 * the admin's default `pending` filter a successful seed used to look
 * like nothing had happened.
 */
function SeedStarterLessonsControl({ onSeeded }: { onSeeded: () => void }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => seedStarterLessons(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['support', 'lessons'] });
      void qc.invalidateQueries({ queryKey: ['support', 'knowledge-preview'] });
      onSeeded();
    },
  });
  const skipNote = mutation.isSuccess ? describeSkipReasons(mutation.data.skipped_reasons) : null;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          Seed starter lessons
        </button>
        {mutation.isSuccess && (
          <span style={{ fontSize: 12, color: 'var(--nx-muted)' }}>
            Seeded {mutation.data.seeded.length}, skipped {mutation.data.skipped.length} (already
            present)
          </span>
        )}
        {skipNote && <span style={{ fontSize: 12, color: 'var(--nx-muted)' }}>{skipNote}</span>}
        {mutation.isError && (
          <span style={{ fontSize: 12, color: 'var(--nx-error)' }}>
            {mutation.error instanceof SupportApiError ? mutation.error.message : String(mutation.error)}
          </span>
        )}
      </div>
      <p style={{ fontSize: 12, color: 'var(--nx-muted)', margin: '4px 0 0' }}>
        Cross-site lessons written from the pilot's incidents. Several signatures carry
        more than one, because the site's failure modes do not come one per kind.
        Safe to run again — a starter that already exists in any state is skipped, never
        re-created.
      </p>
    </div>
  );
}

function CompactionPanel() {
  const qc = useQueryClient();
  const [plan, setPlan] = useState<CompactPreviewResult | CompactApplyResult | null>(null);

  const previewMutation = useMutation({
    mutationFn: (): Promise<CompactPreviewResult> => compactKnowledge(false),
    onSuccess: (result) => setPlan(result),
  });

  const applyMutation = useMutation({
    mutationFn: (): Promise<CompactApplyResult> => compactKnowledge(true),
    onSuccess: (result) => {
      setPlan(result);
      void qc.invalidateQueries({ queryKey: ['support', 'lessons'] });
      void qc.invalidateQueries({ queryKey: ['support', 'knowledge-preview'] });
    },
  });

  const mergeGroups = plan && 'proposed_merges' in plan ? plan.proposed_merges : [];

  return (
    <div style={{ border: '1px solid var(--nx-border)', borderRadius: 'var(--nx-r)', padding: 16 }}>
      <p style={{ fontSize: 13, color: 'var(--nx-muted)' }}>
        This button DOES change data: it runs the outcome rollup immediately, folding raw
        outcome rows into per-signature counters and pruning them. That is safe to do
        unattended because it is lossless for the success rate the assistant reasons over.
        Lesson merging is the part that DESTROYS distinguishing prose, so only that is
        previewed — nothing merges until you apply it below.
      </p>
      <button type="button" disabled={previewMutation.isPending} onClick={() => previewMutation.mutate()}>
        Roll up outcomes &amp; preview merges
      </button>
      {plan && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 13 }}>
            Outcome rollup: folded <strong>{plan.outcome_rollup.folded}</strong> raw row
            {plan.outcome_rollup.folded === 1 ? '' : 's'} into{' '}
            <strong>{plan.outcome_rollup.rollups_touched}</strong> counter
            {plan.outcome_rollup.rollups_touched === 1 ? '' : 's'} (already applied — lossless).
          </p>
          <p style={{ fontSize: 13, fontWeight: plan.lesson_merges_applied ? undefined : 'bold' }}>
            {plan.lesson_merges_applied
              ? `Merged ${'merged_groups' in plan ? plan.merged_groups : 0} lesson group(s).`
              : `${mergeGroups.length} lesson group${mergeGroups.length === 1 ? '' : 's'} proposed for merge.`}
          </p>
          {mergeGroups.length > 0 && !plan.lesson_merges_applied && (
            <>
              <ul style={{ paddingLeft: 18 }}>
                {mergeGroups.map((g, i) => (
                  <li key={i} style={{ marginBottom: 10, fontSize: 13 }}>
                    <div style={{ color: 'var(--nx-muted)' }}>
                      {g.failure_signature} · {g.runbook_id ?? 'no runbook'} ·{' '}
                      {g.site_id ?? 'cross-site'} — merging {g.source_ids.length} lessons (ids{' '}
                      {g.source_ids.join(', ')})
                    </div>
                    <pre
                      style={{
                        whiteSpace: 'pre-wrap',
                        background: 'var(--nx-bg3)',
                        padding: 8,
                        fontSize: 12,
                      }}
                    >
                      {g.merged_body}
                    </pre>
                  </li>
                ))}
              </ul>
              <button type="button" disabled={applyMutation.isPending} onClick={() => applyMutation.mutate()}>
                Apply merges
              </button>
            </>
          )}
        </div>
      )}
      {(previewMutation.isError || applyMutation.isError) && (
        <div style={{ color: 'var(--nx-error)', fontSize: 12, marginTop: 8 }}>
          {String(previewMutation.error ?? applyMutation.error)}
        </div>
      )}
    </div>
  );
}

export function KnowledgePane() {
  const { roles } = useAuth();
  const canAdmin = roles.includes('nexsoss:platform_admin');
  const [params, setParams] = useSearchParams();
  const signature = params.get('signature') ?? KNOWN_FAILURE_SIGNATURES[0];
  const [site, setSite] = useState('');
  const [statusFilter, setStatusFilter] = useState<(typeof LESSON_STATUS_TABS)[number]>(
    canAdmin ? 'pending' : 'approved',
  );

  const sitesQuery = useQuery({ queryKey: ['support', 'sites'], queryFn: listSupportSites });
  const lessonsQuery = useQuery({
    queryKey: ['support', 'lessons', statusFilter, signature],
    queryFn: () => listLessons({ status: statusFilter, signature }),
  });

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Knowledge — what the AI assistant believes</h2>
      <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>
        Doctrine (git-tracked, PR-reviewed) explains what a failure signature means. Lessons
        (below) are drafted from real runbook outcomes and only reach the assistant once a
        platform_admin approves them.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select
          value={signature}
          onChange={(e) => setParams({ signature: e.target.value })}
          aria-label="Failure signature"
        >
          {KNOWN_FAILURE_SIGNATURES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={site} onChange={(e) => setSite(e.target.value)} aria-label="Site (optional)">
          <option value="">All sites</option>
          {(sitesQuery.data ?? []).map((s) => (
            <option key={s.site} value={s.site}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          display: 'grid',
          // `minmax(0, 1fr)`, not `1fr`: see `Tickets/TicketsPane.css`
          // for why the short form runs off the side of the screen the
          // first time a lesson body contains a long unbreakable token.
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 24,
          alignItems: 'start',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <KnowledgePreview signature={signature} site={site} />
        </div>

        <div style={{ minWidth: 0 }}>
          <h3 style={{ marginTop: 0 }}>Lessons</h3>
          <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
            {LESSON_STATUS_TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setStatusFilter(t)}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 'var(--nx-r)',
                  border: '1px solid var(--nx-border)',
                  background: t === statusFilter ? 'var(--nx-bg3)' : 'transparent',
                  color: 'var(--nx-text)',
                }}
              >
                {t}
              </button>
            ))}
          </div>

          {canAdmin && <SeedStarterLessonsControl onSeeded={() => setStatusFilter('approved')} />}

          {lessonsQuery.isLoading ? (
            <SkeletonLoader shape="card-block" count={2} />
          ) : lessonsQuery.isError ? (
            <EmptyState title="Could not load lessons" description={String(lessonsQuery.error)} />
          ) : lessonsQuery.data!.length === 0 ? (
            <EmptyState
              title={`No ${statusFilter} lessons`}
              description={`No ${statusFilter} lessons for ${signature} yet.`}
            />
          ) : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {lessonsQuery.data!.map((l) => (
                <LessonRow key={l.id} lesson={l} canAdmin={canAdmin} />
              ))}
            </ul>
          )}

          {canAdmin && (
            <div style={{ marginTop: 12 }}>
              <ManualLessonForm signature={signature} />
            </div>
          )}
        </div>
      </div>

      {canAdmin && (
        <div style={{ marginTop: 24 }}>
          <h3>Compaction</h3>
          <CompactionPanel />
        </div>
      )}
    </div>
  );
}
